// ============================================
// BRANDED WEIGHT LOSS FOLLOW-UP QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health "Standard Weightloss Follow Up Questionnaire"
// (updated 1/9/2026) — branded GLP products (Wegovy/Ozempic, Zepbound/Mounjaro,
// Foundayo), NOT compounded. Visit Type: weightlessfollowup → weightlossfollowup.
// FE flow string: followup_brandedWeightloss.
//
// Color legend: BLACK = Q/A, BLUE = conditional, PURPLE = named-field, RED =
// disqualifying, PINK = product routing (restart-at-starter → patientPreference
// becomes the starter dose; otherwise the parent preference continues).
//
// Docx caveat: plain-text extraction strips colors; gastric-bypass-6mo "Yes" is
// treated RED (mirrors the intake's RED) — flagged for Beluga verification in
// docs/BACKEND_BRANDED_WEIGHTLOSS_VISIT_PRD.md §Assumptions.

import { BRANDED_GLP_CONSENT_BODY } from './brandedWeightlossData';

// ---------- Sex / pregnancy (PURPLE sex + BLUE pregnancy) ----------

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
  ],
};

const pregnancyQuestion = {
  id: 'pregnancy',
  type: 'single-select',
  question: 'Are you currently pregnant, breastfeeding or planning to become pregnant?',
  showIf: { field: 'sex-at-birth', value: 'female' },
  options: [
    {
      label: 'Yes',
      value: 'yes',
      disqualifies: true,
      disqualifyReason:
        'GLP-1 medications are not safe to use during pregnancy or while breastfeeding. We cannot continue with this submission.',
    },
    {
      label:
        'No and I acknowledge that these medications are not safe to use during pregnancy or while breastfeeding, as they may pose risks to a developing baby.',
      value: 'no-acknowledge',
    },
  ],
};

// ---------- Vitals ----------

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'We use this to recalculate your BMI at each check-in.',
  skipBmiCheck: true,
};

// ---------- Health changes ----------

const healthChangesQuestion = {
  id: 'health-changes',
  type: 'single-select',
  question:
    'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const healthChangesDetailsQuestion = {
  id: 'health-changes-details',
  type: 'textarea',
  question: 'Please tell us more about the changes in your health',
  showIf: { field: 'health-changes', value: 'yes' },
  placeholder: 'Describe your health changes…',
  required: true,
};

// ---------- PURPLE textarea slides (named fields, prefilled) ----------

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea-prefilled',
  question: 'Please identify all your current medical conditions',
  fieldMapping: 'medicalConditions',
  placeholder: 'e.g., Hypertension, Diabetes, etc.',
  required: true,
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea-prefilled',
  question: 'Please list all your current medications including dosages',
  fieldMapping: 'selfReportedMeds',
  placeholder: 'e.g., Lisinopril 10mg daily, Atorvastatin 20mg daily…',
  required: true,
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea-prefilled',
  question: 'Please list all of your known allergies',
  fieldMapping: 'allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
  required: true,
};

// ---------- Screening ----------

const gastricBypassQuestion = {
  id: 'gastric-bypass-6mo',
  type: 'single-select',
  question: 'Have you had a gastric bypass in the past 6 months?',
  options: [
    {
      label: 'Yes',
      value: 'yes',
      disqualifies: true,
      disqualifyReason:
        'GLP-1 medications are not appropriate within 6 months of gastric bypass surgery. Please follow up with your surgical team.',
    },
    { label: 'No', value: 'no' },
  ],
};

const sideEffectsQuestion = {
  id: 'side-effects',
  type: 'single-select',
  question: 'Have you experienced side effects from your current medication?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const sideEffectsDetailsQuestion = {
  id: 'side-effects-details',
  type: 'textarea',
  question: "Please describe the side effects that you've experienced",
  showIf: { field: 'side-effects', value: 'yes' },
  placeholder: 'Describe the side effects…',
  required: true,
};

// ---------- Dose timing branch ----------

const lastDoseTimingQuestion = {
  id: 'last-dose-timing',
  type: 'single-select',
  question: 'When did you last take your prescribed weight loss medication?',
  options: [
    { label: 'Within the past week', value: 'past-week' },
    { label: 'Within the past 2-3 weeks', value: '2-3-weeks' },
    { label: 'It has been over a month', value: 'over-a-month' },
  ],
};

// BLUE — 2-3 weeks → dose-decrease acknowledgment. Decline = RED.
const doseDecreaseAckQuestion = {
  id: 'dose-decrease-ack',
  type: 'single-select',
  question: 'Would you like to continue with a lower starting dose?',
  subtitle:
    "I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at a lower dose, typically about 50% of your previous dose, and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization.",
  showIf: { field: 'last-dose-timing', value: '2-3-weeks' },
  options: [
    { label: 'I understand and agree to the dose reduction', value: 'agree' },
    {
      label: 'I do not agree and do not wish to continue',
      value: 'decline',
      disqualifies: true,
    },
  ],
};

// BLUE — over a month → restart at STARTER dose (PINK: patientPreference
// becomes the starter dose; remaining dose questions are bypassed). Decline = RED.
const restartMedicationQuestion = {
  id: 'restart-medication',
  type: 'single-select',
  question: 'Would you like to continue with a lower starting dose?',
  subtitle:
    "I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at the starter dose and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization.",
  showIf: { field: 'last-dose-timing', value: 'over-a-month' },
  options: [
    { label: 'I understand and agree to the dose reduction', value: 'consent' },
    {
      label: 'I do not agree and do not wish to continue',
      value: 'decline',
      disqualifies: true,
    },
  ],
};

const lastUseDateQuestion = {
  id: 'last-use-date',
  type: 'textarea',
  question: 'Please provide the approximate date (month and day) of your last use of this medication',
  placeholder: 'e.g., May 28',
  required: true,
};

const knowDosageQuestion = {
  id: 'know-dosage',
  type: 'single-select',
  question: "Do you know the dosage that you're taking?",
  options: [
    { label: 'Yes, I know the exact dosage', value: 'yes' },
    { label: "No, I'm not completely sure what my dose is", value: 'no' },
  ],
};

// BLUE — know-dosage = yes.
const currentMedDoseQuestion = {
  id: 'current-med-dose',
  type: 'single-select',
  question: 'Which medication and dose most closely matches your most recent dose?',
  subtitle: 'Keep in mind: Tirzepatide = Mounjaro, Zepbound · Semaglutide = Ozempic, Wegovy',
  showIf: { field: 'know-dosage', value: 'yes' },
  options: [
    { label: 'Semaglutide 0.25mg', value: 'sema-0.25' },
    { label: 'Semaglutide 0.5mg', value: 'sema-0.5' },
    { label: 'Semaglutide 1mg', value: 'sema-1' },
    { label: 'Semaglutide 1.7mg', value: 'sema-1.7' },
    { label: 'Semaglutide 2.4mg', value: 'sema-2.4' },
    { label: 'Tirzepatide 2.5mg', value: 'tirz-2.5' },
    { label: 'Tirzepatide 5mg', value: 'tirz-5' },
    { label: 'Tirzepatide 7.5mg', value: 'tirz-7.5' },
    { label: 'Tirzepatide 10mg', value: 'tirz-10' },
    { label: 'Tirzepatide 12.5mg', value: 'tirz-12.5' },
    { label: 'Tirzepatide 15mg', value: 'tirz-15' },
    // Wegovy Tablets (oral semaglutide). Values mirror the catalog's seeded
    // oral strengths; backend is authoritative for mapping/pricing
    // ("prices will be updated with backend"). Added 2026-07-01.
    { label: 'Wegovy Tablets (oral) 1.5mg', value: 'wegovy-tab-1.5' },
    { label: 'Wegovy Tablets (oral) 4mg', value: 'wegovy-tab-4' },
    { label: 'Wegovy Tablets (oral) 9mg', value: 'wegovy-tab-9' },
    { label: 'Wegovy Tablets (oral) 25mg', value: 'wegovy-tab-25' },
  ],
};

// BLUE — know-dosage = no. Free text per the doc (label photo goes through the
// patient message thread).
const labelPhotoInfoQuestion = {
  id: 'label-photo-info',
  type: 'textarea',
  question:
    "If you're unsure about your prescription details, please upload a clear photo of your medication label. Make sure the entire label is visible, and let us know how many units or milliliters (mL) you are currently using. You can upload the photo directly in your message thread.",
  placeholder: 'e.g., I will upload the label photo; I currently use 0.5 mL weekly…',
  required: true,
};

// ---------- Response & preference ----------

const cravingsQuestion = {
  id: 'cravings-reduction',
  type: 'single-select',
  question: 'Are you experiencing a reduction in food cravings with this dose?',
  options: [
    { label: 'Yes, this is helping', value: 'helping' },
    { label: 'Yes but I need more help', value: 'need-more-help' },
    { label: "No I haven't noticed a difference yet", value: 'no-difference' },
  ],
};

const continuePreferenceQuestion = {
  id: 'continue-preference',
  type: 'single-select',
  question: 'How would you like to continue your treatment?',
  options: [
    { label: 'Stay at the same dose or equivalent dose', value: 'same-dose' },
    {
      label: "Increase the dose if a higher one is available, or continue with my current dose if it's already at the maximum",
      value: 'increase-dose',
    },
    { label: 'Decrease dose', value: 'decrease-dose' },
  ],
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  placeholder: 'Type your questions or concerns here…',
  required: false,
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'GLP-1 and GLP-1/GIP — Informed Consent',
  consentText: BRANDED_GLP_CONSENT_BODY,
  options: [
    { label: 'I have read and understand the above information and I wish to continue', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true },
  ],
};

const checkoutQuestion = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review treatment and pay',
};

// ---------- Question registry ----------

export const brandedWeightlossFollowUpQuestions = {
  sexAtBirth: sexAtBirthQuestion,
  pregnancy: pregnancyQuestion,
  heightWeight: heightWeightQuestion,
  healthChanges: healthChangesQuestion,
  healthChangesDetails: healthChangesDetailsQuestion,
  medicalConditions: medicalConditionsQuestion,
  selfReportedMeds: selfReportedMedsQuestion,
  allergies: allergiesQuestion,
  gastricBypass: gastricBypassQuestion,
  sideEffects: sideEffectsQuestion,
  sideEffectsDetails: sideEffectsDetailsQuestion,
  lastDoseTiming: lastDoseTimingQuestion,
  doseDecreaseAck: doseDecreaseAckQuestion,
  restartMedication: restartMedicationQuestion,
  lastUseDate: lastUseDateQuestion,
  knowDosage: knowDosageQuestion,
  currentMedDose: currentMedDoseQuestion,
  labelPhotoInfo: labelPhotoInfoQuestion,
  cravings: cravingsQuestion,
  continuePreference: continuePreferenceQuestion,
  additionalQuestions: additionalQuestionsQuestion,
  mainConsent: mainConsentQuestion,
  checkout: checkoutQuestion,
};

export const brandedWeightlossFollowUpFlow = [
  'sex-at-birth',
  'pregnancy',
  'height-weight',
  'health-changes',
  'health-changes-details',
  'medical-conditions',
  'self-reported-meds',
  'allergies',
  'gastric-bypass-6mo',
  'side-effects',
  'side-effects-details',
  'last-dose-timing',
  'dose-decrease-ack',
  'restart-medication',
  'last-use-date',
  'know-dosage',
  'current-med-dose',
  'label-photo-info',
  'cravings-reduction',
  'continue-preference',
  'additional-questions',
  'main-consent',
  'checkout',
];

export const getBrandedWeightlossFollowUpQuestionById = (id) =>
  Object.values(brandedWeightlossFollowUpQuestions).find((q) => q.id === id);

// Restart-at-starter bypass: per the doc, when the patient agrees to restart
// at the starter dose, the dose-detail series is skipped and the flow jumps to
// "What other information or questions do you have for the doctor?".
const RESTART_BYPASSED_IDS = [
  'last-use-date',
  'know-dosage',
  'current-med-dose',
  'label-photo-info',
  'cravings-reduction',
  'continue-preference',
];

// BLUE conditionals:
//   pregnancy            ← sex-at-birth='female' (Yes → RED)
//   health-changes-details ← health-changes='yes'
//   side-effects-details ← side-effects='yes'
//   dose-decrease-ack    ← last-dose-timing='2-3-weeks' (decline → RED)
//   restart-medication   ← last-dose-timing='over-a-month' (decline → RED;
//                          consent → PINK starter dose + bypass dose series)
//   current-med-dose     ← know-dosage='yes'
//   label-photo-info     ← know-dosage='no'
export const buildDynamicBrandedWeightlossFollowUpFlow = (answers /* , userData */) => {
  const restartAtStarter = answers['restart-medication'] === 'consent';
  const flow = [];
  for (const questionId of brandedWeightlossFollowUpFlow) {
    const question = getBrandedWeightlossFollowUpQuestionById(questionId);
    if (!question) continue;

    if (restartAtStarter && RESTART_BYPASSED_IDS.includes(questionId)) continue;

    if (questionId === 'label-photo-info') {
      if (answers['know-dosage'] !== 'no') continue;
    }

    if (question.showIf) {
      const { field, value, includes } = question.showIf;
      const current = answers[field];
      if (includes !== undefined) {
        if (!Array.isArray(current) || !current.includes(includes)) continue;
      } else if (current !== value) {
        continue;
      }
    }

    flow.push(question);
  }
  return flow;
};
